import React, { useEffect, useState, useMemo } from 'react'
import BarChart from '../../components/chartTypes/Barchart';
import MultiSelectCheckboxGroup from '../../components/CheckBoxFilter';
import { TableBranchStateEMI, TableBranchState, ProductTable } from '../../components/TableBranchState';
import { useTranslation } from 'react-i18next';
import { sixMonthsAgoFormatted, twoDaysAgoFormatted } from '../../constants/intex';
import { generateOrginationByProduct } from '../../services/originationByProductType';
import { listBranches } from '../../services/filterApis';
import Loader from '../../components/Loader';
import Table2 from '../../components/Table2';

// Updated Branch colors for comparative mode
const BRANCH_COLORS = [
  { 
    bgColor: '#E3F2FD',  // Light Blue background
    textColor: '#0D47A1', // Dark Blue text
    borderColor: '#90CAF9' // Blue border
  },
  { 
    bgColor: '#E8F5E9',  // Light Green background
    textColor: '#1B5E20', // Dark Green text
    borderColor: '#A5D6A7' // Green border
  },
  { 
    bgColor: '#FFF3E0',  // Light Orange background
    textColor: '#E65100', // Dark Orange text
    borderColor: '#FFCC80' // Orange border
  }
];

type BranchColor = {
  bgColor: string;
  textColor: string;
  borderColor: string;
};

const NewOriginationByProductType = () => {
  const { t } = useTranslation();

  const filters = [
    { key: "state", type: "select", label: t("filters.state")},
    { key: "cluster", type: "select", label: t("filters.cluster") },
    {
      key: "displaymode",
      type: "singleSelect",
      label: t("filters.displaymode"),
    },
    { key: "branch", type: "select", label: t("filters.branch")},
    { key: "period", type: "singleSelect", label: t("filters.period") },
    { key: "fromDate", type: "date", label: t("filters.fromDate") },
    { key: "toDate", type: "date", label:t("filters.toDate")},
  ];

  const [pendingFilters, setPendingFilters] = useState<Record<string, string[]>>(() => ({
    period: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"]
  }));

  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(() => ({
    period: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"]
  }));

  const [applyFilter, setApplyFilter] = useState<number>(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDownloadDisabled, setIsDownloadDisabled] = useState<boolean>(false);
  
  const [orginationEwiByState, setOrginationEwiByState] = useState<any[]>([]);
  const [orginationEmiByState, setOrginationEmiByState] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [barChartData, setBarChartData] = useState<{
    labels: string[];
    dataSets: { label: string; data: number[]; backgroundColor: string }[];
  }>({
    labels: [],
    dataSets: [],
  });

  const [comparativeLoading, setComparativeLoading] = useState<boolean>(false);
  const [comparativeTables, setComparativeTables] = useState<any[][]>([]);
  const [branchList, setBranchList] = useState<{ branch_id: string; branch_name: string }[]>([]);
  const [branchColors, setBranchColors] = useState<Record<string, BranchColor>>({});

  // Fetch branches when state changes
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await listBranches(selectedFilters.state || []);
        const branches = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
          ? response
          : [];
        setBranchList(branches);
      } catch (error) {
        console.error("Error loading branches", error);
        setBranchList([]);
      }
    };

    fetchBranches();
  }, [selectedFilters.state]);

  // Create branch ID to name map
  const branchIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(branchList)) {
      branchList.forEach((branch) => {
        const id = branch?.branch_id;
        const name = branch?.branch_name;
        if (id && name) {
          map[id] = name;
        }
      });
    }
    return map;
  }, [branchList]);

  const getBranchName = (branchId: string): string => {
    if (branchIdToNameMap[branchId]) return branchIdToNameMap[branchId];
    const branch = branchList.find((b) => b.branch_id === branchId);
    if (branch) return branch.branch_name || branchId;
    return branchId;
  };

  //handle filter change
  const handleFilterChange = (updatedValues: Record<string, string[]>) => {
    setPendingFilters(updatedValues);
  };

  const transformToTableData = (apiData: any[]) => {
    const headers = ["Product", "", ...apiData.map((entry) => entry.period)];
  
    const productMap: Record<
      string,
      {
        drawdown_amt: (string | number)[];
        percentage_drawdown_amt: (string | number)[];
        loan_count: (string | number)[];
      }
    > = {};
  
    apiData.forEach((entry) => {
      entry.product_details.forEach((product: any) => {
        const name = product.product_name;
  
        if (!productMap[name]) {
          productMap[name] = {
            drawdown_amt: [],
            percentage_drawdown_amt: [],
            loan_count: [],
          };
        }
  
        productMap[name].drawdown_amt.push(product.drawdown_amt_formatted || 0);
        productMap[name].percentage_drawdown_amt.push(
          product.percentage_drawdown_amt !== null
            ? `${product.percentage_drawdown_amt}%`
            : "0%"
        );
        productMap[name].loan_count.push(product.loan_count || 0);
      });
    });
  
    const tableData: any[] = [];
  
    for (const product in productMap) {
      const rowSpan = 3; // always 3 types per product
  
      tableData.push({
        product,
        type: "Drawdown Amount",
        values: productMap[product].drawdown_amt,
        rowSpan,
      });
      tableData.push({
        product,
        type: "% Drawdown Amount",
        values: productMap[product].percentage_drawdown_amt,
      });
      tableData.push({
        product,
        type: "#Loans",
        values: productMap[product].loan_count,
      });
    }
  
    return { headers, tableData };
  };

  const transformToBarChartData = (apiData: any[]) => {
    const labels = apiData.map((entry) => entry.period);
  
    const productMap: Record<string, number[]> = {};
  
    apiData.forEach((entry) => {
      entry.product_details.forEach((product: any) => {
        const name = product.product_name;
  
        if (!productMap[name]) {
          productMap[name] = [];
        }
  
        productMap[name].push(product.ticket_size || 0);
      });
    });
  
    const colors = ["#70e000", "#38b000", "#008000", "#1e6091", "#ff9f1c", "#ef476f"];
    const dataSets = Object.entries(productMap).map(([name, data], index) => ({
      label: name,
      data,
      backgroundColor: colors[index % colors.length],
    }));
  
    return { labels, dataSets };
  };

  // Handle apply click with validation
  const handleApplyClick = () => {
    const displaymodeRaw = pendingFilters.displaymode?.[0] || "consolidate";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const selectedBranches = pendingFilters.branch || [];
    const isComparative = displaymode === "comparative" || displaymode === "2";

    if (isComparative) {
      if (selectedBranches.length < 2 || selectedBranches.length > 3) {
        setValidationError(
          "Please select minimum two and maximum three branches for comparative mode."
        );
        setIsValid(false);
        setIsDownloadDisabled(true);
        
        return;
      }
      
      // Assign colors to branches
      const colors: Record<string, BranchColor> = {};
      selectedBranches.forEach((branchId, index) => {
        colors[branchId] = BRANCH_COLORS[index % BRANCH_COLORS.length];
      });
      setBranchColors(colors);
    }

    setSelectedFilters(pendingFilters);
    setValidationError("");
    setIsValid(true);
    setIsDownloadDisabled(false);
    setApplyFilter((prev) => prev + 1);
  };

  //handle cancel click
  const handleCancelClick = () => {
    setSelectedFilters({
      period: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"]
    });
    setPendingFilters({
      period: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"]
    });
    setApplyFilter(0);
    setBranchColors({});
  };

  //Api to get Drawdown Amount by Product table data
  const getGenerateOrginationByProduct = async () => {
    const displaymodeRaw = selectedFilters.displaymode?.[0] || "1";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const isComparative = displaymode === "comparative" || displaymode === "2";

    setLoading(true);

    try {
      if (isComparative) {
        setComparativeLoading(true);
        const selectedBranches = selectedFilters.branch || [];

        const branchPromises = selectedBranches.map((branchId) =>
          generateOrginationByProduct({
            ...selectedFilters,
            branch: [branchId],
          })
        );

        const branchResponses = await Promise.all(branchPromises);
        const comparativeData = branchResponses.map((res) =>
          Array.isArray(res.data) ? res.data : []
        );

        setComparativeTables(comparativeData);

        // Fetch data for period keys (without branch filter)
        const periodResponse = await generateOrginationByProduct({
          ...selectedFilters,
          branch: [],
        });

        const periodData = Array.isArray(periodResponse.data)
          ? periodResponse.data
          : [];

        const { headers, tableData } = transformToTableData(periodData);
        const { labels, dataSets } = transformToBarChartData(periodData);

        setHeaders(headers);
        setTableData(tableData);
        setBarChartData({ labels, dataSets });
      } else {
        const response = await generateOrginationByProduct(selectedFilters);
        const { headers, tableData } = transformToTableData(response.data);
        const { labels, dataSets } = transformToBarChartData(response.data);

        setHeaders(headers);
        setTableData(tableData);
        setBarChartData({ labels, dataSets });
      }
    } catch (err) {
      setError("Failed to fetch data");
      console.error(err);
    } finally {
      setLoading(false);
      setComparativeLoading(false);
    }
  };

  useEffect(() => {
    getGenerateOrginationByProduct();
  }, [applyFilter]);

  // Format for comparative table
  // Format for comparative table
const formatComparativeTable = () => {
  if (!comparativeTables.length || !selectedFilters.branch?.length) return null;

  // Create combined data structure
  const branchDataMap = new Map<string, any[]>();
  (selectedFilters.branch || []).forEach((branchId, index) => {
    branchDataMap.set(branchId, comparativeTables?.[index] || []);
  });

  // Get all periods from the first branch data
  const allPeriods = comparativeTables[0]?.map((item) => item.period) || [];

  // Create combined data for each product
  const products = new Set<string>();
  comparativeTables.forEach((table) => {
    table.forEach((periodData) => {
      periodData.product_details.forEach((product: any) => {
        products.add(product.product_name);
      });
    });
  });

  const productList = Array.from(products);

  // Create table data for each product type
  const createComparativeRows = (type: string) => {
    return productList.map((product) => {
      const row: any = { product, type };

      allPeriods.forEach((period) => {
        (selectedFilters.branch || []).forEach((branchId) => {
          const branchData = branchDataMap.get(branchId) || [];
          const periodData = branchData.find((d) => d.period === period);
          const productData = periodData?.product_details.find(
            (p: any) => p.product_name === product
          );

          let value = "--";
          if (productData) {
            if (type === "Drawdown Amount") {
              value = productData.drawdown_amt_formatted || "--";
            } else if (type === "% Drawdown Amount") {
              value = productData.percentage_drawdown_amt !== null 
                ? `${productData.percentage_drawdown_amt}%` 
                : "--";
            } else if (type === "#Loans") {
              value = productData.loan_count || "--";
            }
          }

          row[`${branchId}_${period}`] = value;
        });
      });

      return row;
    });
  };

  const drawdownRows = createComparativeRows("Drawdown Amount");
  const percentageRows = createComparativeRows("% Drawdown Amount");
  const loanCountRows = createComparativeRows("#Loans");

  // Combine all rows
  const combinedData = [];
  for (let i = 0; i < productList.length; i++) {
    combinedData.push(drawdownRows[i]);
    combinedData.push(percentageRows[i]);
    combinedData.push(loanCountRows[i]);
  }

  // Create columns
  const columns = [
    {
      Header: "Product",
      accessor: "product",
      sticky: "left",
      style: {
        fontWeight: "bold",
        borderRight: "1px solid #000",
        backgroundColor: "#f8f9fa", // Light gray for product column
      },
    },
    {
      Header: "Type",
      accessor: "type",
      sticky: "left",
      style: {
        borderRight: "1px solid #000",
        backgroundColor: "#f8f9fa", // Light gray for type column
      },
    },
    ...allPeriods.flatMap((period) => ({
      Header: period,
      headerStyle: {
        borderBottom: "1px solid #000",
        borderRight: "1px solid #000",
        backgroundColor: "#ffffff", // White for period headers
      },
      columns: (selectedFilters.branch || []).map((branchId) => ({
        Header: getBranchName(branchId),
        accessor: `${branchId}_${period}`,
        headerStyle: {
          backgroundColor: "#ffffff", // White for branch headers
          color: "#000000", // Black text for headers
        },
        style: {
          backgroundColor: branchColors[branchId]?.bgColor || "#f5f5f5", // Colored for data cells only
          color: branchColors[branchId]?.textColor || "inherit",
          borderRight: "1px solid #ddd",
        },
      })),
    })),
  ];

  return (
    <Table2
      columns={columns}
      data={combinedData}
      tableHead={t('originationProductType.Drawdown Amount by Product')}
      type="1"
      loading={comparativeLoading || loading}
      isComparative={true}
      branchCount={selectedFilters.branch?.length || 0}
      branchColors={branchColors}
      branchIds={selectedFilters.branch || []}
    />
  );
};
  return (
    <div>
      {/* Page Content */}
      <div className="page-content">
        {/* Dynamic Grey Header */}
        <div className="header">
          {t('originationProductType.pageTitle')}
        </div>

        <div className="content px-3">
          <MultiSelectCheckboxGroup 
            filters={filters} 
            selectedValues={pendingFilters} 
            onChange={handleFilterChange} 
            isLoading={[loading, comparativeLoading]}
            buttons={[
              { label: t("buttons.apply"), onClick: () => handleApplyClick()},
              { label: t("buttons.cancel"), onClick: () => handleCancelClick()},
              { 
                label: t("buttons.download"), 
                onClick: () => alert("download btn clicked"),
                disabled: isDownloadDisabled
              },
            ]}
          />
          {/* Show validation error if exists */}
          {validationError && (
            <div
              style={{
                color: "#B00020",
                backgroundColor: "#FDECEA",
                padding: "12px 16px",
                border: "1px solid #F5C6CB",
                borderRadius: "6px",
                fontWeight: "500",
                marginTop: "10px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              ⚠️ {validationError}
            </div>
          )}
        </div>

        {/* New Origination EWI by branch */}
        <div className="p-3">
          {!validationError && (
          <div style={{"width": "100%", "overflow": "auto"}}>
            {selectedFilters.displaymode?.[0]?.trim().toLowerCase() === "2" ? (
              formatComparativeTable()
            ) : (
              <>
                <h1 style={{ fontSize: 15, fontWeight: 600, fontFamily: 'Arial', fontStyle: 'normal', textAlign: "start", paddingLeft: "15px", color: '#14213d',}}>
                  {t('originationProductType.Drawdown Amount by Product')}
                </h1>
                <ProductTable data={tableData} headers={headers} loading={loading} />
              </>
            )}
          </div>
          )}
        </div>

        <div className="mt-5 p-5" style={{marginTop: "20px"}}>
          {!validationError && (
          <BarChart 
              title={t('originationProductType.title')}
              labels={barChartData.labels}
              dataSets={barChartData.dataSets}
              xLabel={t('originationProductType.xLabel')}
              yLabel={t('originationProductType.yLabel')}
              barThickness={10}
          />
          )}
        </div>
      </div>
    </div>
  )
}

export default NewOriginationByProductType;